/**
 * Класс Tooltip предоставляет функциональность для отображения всплывающих подсказок (тултипов)
 * при наведении или клике на элементы, соответствующие заданным селекторам.
 * Также тултип автоматически закрывается при:
 * - прокрутке страницы
 * - изменении размера окна
 *
 * @example
 * // Инициализация с настройками по умолчанию
 * new Tooltip();
 *
 * // С кастомными селекторами и параметрами
 * new Tooltip('.j-custom-tooltip', '.j-custom-tooltip-trigger', '.j-custom-tooltip-content', 15, 'top');
 */

export class Tooltip {
    private currentTooltip: HTMLElement | null = null;

    /**
     * @param {string} container - класс контейнера, содержащего триггер и тултип. По умолчанию 'j-tooltip'.
     * @param {string} trigger   - класс элемента, при наведении/клике на который показывается тултип. По умолчанию 'j-tooltip-trigger'.
     * @param {string} content   - класс элемента с содержимым тултипа. По умолчанию 'j-tooltip-content'.
     * @param {number} offset            - Расстояние в пикселях между тултипом и триггером. По умолчанию 20.
     * @param {'auto' | 'top' | 'bottom'} position - Желаемое положение тултипа. По умолчанию 'top'.
     */
    // eslint-disable-next-line max-params
    constructor(private readonly container: string = '.j-tooltip',
                private readonly trigger: string = '.j-tooltip-trigger',
                private readonly content: string = '.j-tooltip-content',
                private readonly offset: number = 20,
                private readonly position: 'auto' | 'top' | 'bottom' = 'top') {
        this.init();
    }

    private init(): void {
        document.addEventListener('mouseover', this.handleMouseOver);
        document.addEventListener('mouseout', this.handleMouseOut);
        document.addEventListener('click', this.handleClick);
        document.addEventListener('scroll', this.handleScroll, {passive: true});
        window.addEventListener('resize', this.handleResize);
    }

    private handleMouseOver: (event: Event) => void = (event: Event): void => {
        const tooltip = this.resolveTooltip(event);

        if (tooltip) {
            this.show(event, tooltip);
        }
    };

    private handleMouseOut: (event: Event) => void = (event: Event): void => {
        const target = event.target as HTMLElement;

        if (this.currentTooltip && !this.isTooltipRelated(target)) {
            this.hide();
        }
    };

    private handleClick: (event: Event) => void = (event: Event): void => {
        const tooltip = this.resolveTooltip(event);
        const target = event.target as HTMLElement;

        if (tooltip) {
            event.stopPropagation();
            this.show(event, tooltip);
        } else if (this.currentTooltip && !this.isTooltipRelated(target)) {
            this.hide();
        }
    };

    private handleScroll: () => void = (): void => {
        if (this.currentTooltip) {
            this.hide();
        }
    };

    private handleResize: () => void = (): void => {
        if (this.currentTooltip) {
            this.hide();
        }
    };

    private resolveTooltip(event: Event): HTMLElement | null {
        const target = event.target as HTMLElement;
        const trigger = target.closest(this.trigger);

        return trigger?.closest(this.container)?.querySelector(this.content) ?? null;
    }

    private isTooltipRelated(target: HTMLElement): boolean {
        return Boolean(target.closest(this.trigger) ||
            target.closest(this.content));
    }

    // eslint-disable-next-line max-statements
    private show(event: Event, tooltip: HTMLElement): void {
        this.hide();

        tooltip.style.opacity = '1';
        tooltip.style.pointerEvents = 'auto';
        this.currentTooltip = tooltip;

        const target = event.target as HTMLElement;
        const targetRect = target.getBoundingClientRect();
        const tooltipRect = tooltip.getBoundingClientRect();

        let x = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
        let y = 0;
        let finalPosition = 'auto';

        x = Math.max(10, Math.min(x, window.innerWidth - tooltipRect.width - 10));

        tooltip.classList.remove('top', 'bottom');

        if (this.position === 'auto') {
            const spaceAbove = targetRect.top - this.offset;
            const spaceBelow = window.innerHeight - targetRect.bottom - this.offset;

            if (spaceBelow >= tooltipRect.height || spaceBelow >= spaceAbove) {
                finalPosition = 'bottom';
                y = targetRect.bottom + this.offset;
            } else {
                finalPosition = 'top';
                y = targetRect.top - tooltipRect.height - this.offset;
            }
        } else if (this.position === 'top') {
            const spaceAbove = targetRect.top - this.offset;

            if (spaceAbove >= tooltipRect.height) {
                finalPosition = 'top';
                y = targetRect.top - tooltipRect.height - this.offset;
            } else {
                finalPosition = 'bottom';
                y = targetRect.bottom + this.offset;
            }
        } else {
            const spaceBelow = window.innerHeight - targetRect.bottom - this.offset;

            if (spaceBelow >= tooltipRect.height) {
                finalPosition = 'bottom';
                y = targetRect.bottom + this.offset;
            } else {
                finalPosition = 'top';
                y = targetRect.top - tooltipRect.height - this.offset;
            }
        }

        tooltip.classList.add(finalPosition);
        tooltip.style.left = `${x}px`;
        tooltip.style.top = `${y}px`;
    }

    private hide(): void {
        if (this.currentTooltip) {
            this.currentTooltip.style.opacity = '0';
            this.currentTooltip.style.pointerEvents = 'none';
            this.currentTooltip = null;
        }
    }
}
